import api from '../common/utils/api.js';
import { mutateAdmin, queryAdmin } from '../graphql-client.js';
import type { AuthAction, ForgotPasswordAction, ResetPasswordAction } from './actions.js';
import type { PersonalAccessToken } from './types.js';
import { parseTrackingCookie } from './util/cookie.js';

export async function getUser() {
  const params = {
    method: 'GET',
    path: 'users/me'
  };

  return api(params);
}

export async function registerUser(user: any) {
  const params = {
    method: 'POST',
    path: 'register',
    data: {
      ...user,
      tracking: parseTrackingCookie(document.cookie)
    }
  };

  return api(params);
}

export async function updateUser({ payload }: { payload: any }) {
  const params = {
    method: 'PUT',
    path: 'users/me',
    data: payload
  };

  return api(params).then((res) => res.data);
}

export async function loginUser(payload: any) {
  const params = {
    method: 'POST',
    path: 'login',
    data: payload
  };

  return api(params);
}

export async function logoutUser() {
  const params = {
    method: 'POST',
    path: 'logout'
  };

  return api(params);
}

export async function forgotPassword({ payload, meta }: ForgotPasswordAction) {
  if (meta?.executeRecaptcha) {
    const recaptchaToken = await meta.executeRecaptcha();
    const params = {
      method: 'POST',
      path: 'forgot-password',
      data: {
        ...payload,
        recaptchaToken
      }
    };

    return api(params);
  }
}

export async function resetPassword({ payload }: ResetPasswordAction) {
  const { token, password } = payload;
  const params = {
    method: 'POST',
    path: `reset-password?auth=${token}`,
    data: { password }
  };

  return api(params);
}

export async function callCli({ port }: { port: string }) {
  const params = {
    method: 'GET',
    url: `http://localhost:${port}`
  };

  return api(params);
}

export async function getPersonalAccessTokenList() {
  const query = /* GraphQL */ `
    query {
      tokens: getPersonalAccessTokenList {
        id
        name
        userId
        lastUsed
      }
    }
  `;
  return queryAdmin<{ tokens: PersonalAccessToken[] }>(query)();
}

export const CREATE_PERSONAL_ACCESS_TOKEN_MUTATION = /* GraphQL */ `
  mutation ($name: String!) {
    accessToken: createPersonalAccessToken(name: $name) {
      id
      name
      accessToken
    }
  }
`;

export async function createPersonalAccessTokenMutation(payload: { name: string }) {
  return mutateAdmin<{
    accessToken: PersonalAccessToken;
  }>(CREATE_PERSONAL_ACCESS_TOKEN_MUTATION)(payload);
}

export const DELETE_PERSONAL_ACCESS_TOKEN_MUTATION = /* GraphQL */ `
  mutation ($id: ID!) {
    deleted: deletePersonalAccessToken(id: $id)
  }
`;

export async function deletePersonalAccessTokenMutation(payload: { id: string }) {
  return mutateAdmin<{ deleted: boolean }>(DELETE_PERSONAL_ACCESS_TOKEN_MUTATION)(payload);
}

export const VALIDATE_EMAIL_MUTATION = /* GraphQL */ `
  mutation ($token: String!) {
    success: validateEmail(token: $token)
  }
`;

export async function validateEmailMutation(payload: { token: string }) {
  return mutateAdmin<{ success: boolean }>(VALIDATE_EMAIL_MUTATION)(payload);
}

export const RESEND_VALIDATION_EMAIL_MUTATION = /* GraphQL */ `
  mutation {
    success: resendValidationEmail
  }
`;

export async function resendValidationEmail() {
  return mutateAdmin<{ success: boolean }>(RESEND_VALIDATION_EMAIL_MUTATION)();
}
